import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select, Textarea } from '@artosera/ui';
import {
  formatNumber,
  formatPrice,
  POINTS_DE_BASE,
  type Devise,
  type Langue,
} from '@artosera/shared';
import { useContact, useContacts } from '../../api/contacts.js';
import { useOeuvres, useEditionsOeuvre } from '../../api/oeuvres.js';
import { useReglagesNegociation } from '../../api/presentation.js';
import { useCreerOffre, useModifierOffre, type OffrePresentee } from '../../api/offres.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { SuggestionPrixApprouve } from '../presentation/SuggestionPrixApprouve.js';
import { Field } from '../Field.js';
import { ecartAuPrixDemande } from './syntheseOffre.js';
import styles from './OffreDetailPage.module.css';
import etapes from './NouvelleOffreDialog.module.css';

export interface NouvelleOffreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactInitial?: { id: string; nom: string };
  oeuvreInitiale?: {
    id: string;
    titre: string;
    prixPublicCentimes: number | null;
    editionSuivie?: boolean;
  };
  offreInitiale?: OffrePresentee;
  lignesInitiales?: LigneChoisie[];
  /** Numéro de l'offre dont celle-ci reprend les œuvres pour faire une autre proposition. */
  contrePropositionDe?: string;
}
interface LigneChoisie {
  oeuvreId: string;
  titre: string;
  prixProposeCentimes: number;
  /** Prix demandé (catalogue ou exemplaire) : le point de comparaison du prix proposé. */
  prixPublicCentimes?: number | null;
  devise?: Devise;
  /** Pour retrouver la remise maximale propre à l'artiste. */
  artisteId?: string;
  exemplaireId?: string;
  editionSuivie?: boolean;
}
type Etape = 'oeuvres' | 'contact' | 'montant';

export function dateOffreParDefaut() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function NouvelleOffreDialog({
  open,
  onOpenChange,
  contactInitial,
  oeuvreInitiale,
  offreInitiale,
  lignesInitiales,
  contrePropositionDe,
}: NouvelleOffreDialogProps) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langueInterface: Langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const [contact, setContact] = useState(
    contactInitial ??
      (offreInitiale ? { id: offreInitiale.contactId, nom: offreInitiale.contactNom } : null),
  );
  const [rechercheContact, setRechercheContact] = useState('');
  const [rechercheOeuvre, setRechercheOeuvre] = useState('');
  const [lignes, setLignes] = useState<LigneChoisie[]>(
    lignesInitiales ??
      offreInitiale?.lignes.map((l) => ({
        oeuvreId: l.oeuvreId,
        titre: l.cartel.titre,
        editionSuivie: Boolean(l.exemplaireId),
        prixProposeCentimes: l.prixProposeCentimes,
        prixPublicCentimes: l.prixPublicCentimes,
        ...(l.exemplaireId ? { exemplaireId: l.exemplaireId } : {}),
      })) ??
      (oeuvreInitiale
        ? [
            {
              oeuvreId: oeuvreInitiale.id,
              titre: oeuvreInitiale.titre,
              editionSuivie: oeuvreInitiale.editionSuivie,
              prixPublicCentimes: oeuvreInitiale.prixPublicCentimes,
              prixProposeCentimes: oeuvreInitiale.prixPublicCentimes ?? 0,
            },
          ]
        : []),
  );
  // Le parcours : œuvre → contact → montant. Une donnée déjà connue (contact de la
  // fiche d'où l'on vient, œuvres d'une sélection) ouvre le parcours à l'étape suivante.
  const parcours: Etape[] = contactInitial
    ? ['oeuvres', 'montant']
    : ['oeuvres', 'contact', 'montant'];
  const [etape, setEtape] = useState<Etape>(() =>
    offreInitiale || (lignes.length && contact) ? 'montant' : lignes.length ? 'contact' : 'oeuvres',
  );
  const [validite, setValidite] = useState(offreInitiale?.validiteJusquau ?? dateOffreParDefaut());
  const [message, setMessage] = useState(offreInitiale?.message ?? '');
  const [langue, setLangue] = useState<Langue | null>(offreInitiale?.langue ?? null);
  const fiche = useContact(galerieSlug, contact?.id ?? null);
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheContact, parPage: 20 },
    { enabled: rechercheContact.trim().length >= 2 },
  );
  const oeuvres = useOeuvres(
    galerieSlug,
    { q: rechercheOeuvre, statut: ['disponible', 'reservee', 'en_depot', 'pretee'], parPage: 20 },
    { enabled: rechercheOeuvre.trim().length >= 2 },
  );
  // La remise maximale vient des réglages de négociation (add-on rendez-vous client).
  const reglages = useReglagesNegociation(
    galerieSlug,
    galerieAModule(galerie, 'rendez-vous-client') && galerieALaction(galerie, 'presentation:read'),
  );
  const creer = useCreerOffre(galerieSlug);
  const modifier = useModifierOffre(galerieSlug, offreInitiale?.id ?? '');
  const mutation = offreInitiale ? modifier : creer;
  const editionsSansExemplaire = lignes.some((l) => l.editionSuivie && !l.exemplaireId);
  const valide: Record<Etape, boolean> = {
    oeuvres: lignes.length > 0 && !editionsSansExemplaire,
    contact: contact !== null,
    montant: Boolean(contact) && lignes.length > 0 && Boolean(validite) && !editionsSansExemplaire,
  };
  const position = parcours.indexOf(etape);
  const derniere = position === parcours.length - 1;
  const soumettre = async () => {
    if (!contact || !lignes.length || editionsSansExemplaire) return;
    const corps = {
      contactId: contact.id,
      lignes: lignes.map((l) => ({
        oeuvreId: l.oeuvreId,
        prixProposeCentimes: l.prixProposeCentimes,
        ...(l.exemplaireId ? { exemplaireId: l.exemplaireId } : {}),
      })),
      validiteJusquau: validite,
      message,
      langue: langue ?? fiche.data?.contact.langue ?? 'fr',
    };
    try {
      const reponse = offreInitiale
        ? await modifier.mutateAsync(corps)
        : await creer.mutateAsync(corps);
      onOpenChange(false);
      if (!offreInitiale) navigate(`/${galerieSlug}/offres/${reponse.offre.id}`);
    } catch {
      /* L'erreur est présentée par la mutation. */
    }
  };
  const remiseMaxDe = (ligne: LigneChoisie): number | null => {
    const donnees = reglages.data?.reglages;
    if (!donnees) return null;
    return (
      donnees.parArtiste.find((a) => a.artisteId === ligne.artisteId)?.remiseMaxPointsDeBase ??
      donnees.remiseMaxPointsDeBase
    );
  };
  const remplacer = (index: number, changement: Partial<LigneChoisie>) =>
    setLignes((courantes) => courantes.map((l, i) => (i === index ? { ...l, ...changement } : l)));
  // Comme l'API : la devise de l'offre est celle de sa première œuvre.
  const devise: Devise = offreInitiale?.devise ?? lignes[0]?.devise ?? 'EUR';
  const total = lignes.reduce((somme, l) => somme + l.prixProposeCentimes, 0);
  const aide = contrePropositionDe
    ? t('regie:offres.nouvelle.contreProposition', { numero: contrePropositionDe })
    : t(`regie:offres.nouvelle.etapes.${etape}.aide`);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t(offreInitiale ? 'regie:offres.fiche.modifier' : 'regie:offres.nouvelle.titre')}
      closeLabel={t('commun:actions.fermer')}
      description={aide}
      footer={
        <div className={etapes.pied}>
          {position > 0 ? (
            <Button variant="ghost" onClick={() => setEtape(parcours[position - 1]!)}>
              {t('regie:offres.nouvelle.retour')}
            </Button>
          ) : null}
          {derniere ? (
            <Button
              loading={mutation.isPending}
              disabled={!valide.montant}
              className={styles.bouton}
              onClick={() => void soumettre()}
            >
              {t(offreInitiale ? 'commun:actions.enregistrer' : 'regie:offres.nouvelle.creer')}
            </Button>
          ) : (
            <Button
              disabled={!valide[etape]}
              className={styles.bouton}
              onClick={() => setEtape(parcours[position + 1]!)}
            >
              {t('regie:offres.nouvelle.continuer')}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.formulaire}>
        <ol className={etapes.etapes} aria-label={t('regie:offres.nouvelle.parcours')}>
          {parcours.map((cle, i) => (
            <li
              key={cle}
              className={etapes.etape}
              aria-current={cle === etape ? 'step' : undefined}
              data-fait={i < position ? 'true' : undefined}
            >
              <span className={etapes.numero} aria-hidden="true">
                {i + 1}
              </span>
              {t(`regie:offres.nouvelle.etapes.${cle}.titre`)}
            </li>
          ))}
        </ol>
        {contact && etape !== 'contact' ? (
          <p className={etapes.recap}>
            {t('regie:offres.nouvelle.recap', { contact: contact.nom })}
          </p>
        ) : null}
        {etape === 'oeuvres' ? (
          <>
            <Field htmlFor="offre-recherche" label={t('regie:offres.nouvelle.oeuvre')}>
              <Input
                id="offre-recherche"
                type="search"
                value={rechercheOeuvre}
                onChange={(e) => setRechercheOeuvre(e.target.value)}
              />
            </Field>
            {oeuvres.data?.oeuvres
              .filter((o) => !lignes.some((l) => l.oeuvreId === o.id))
              .map((o) => (
                <Button
                  variant="ghost"
                  key={o.id}
                  onClick={() => {
                    setLignes([
                      ...lignes,
                      {
                        oeuvreId: o.id,
                        titre: o.titre,
                        artisteId: o.artisteId,
                        devise: o.devise,
                        editionSuivie: o.type === 'edition' && o.tirage?.exemplairesSuivis === true,
                        prixPublicCentimes: o.prixPublicCentimes,
                        prixProposeCentimes: o.prixPublicCentimes ?? 0,
                      },
                    ]);
                    setRechercheOeuvre('');
                  }}
                >
                  {o.titre} · {o.artisteNom}
                </Button>
              ))}
            {oeuvres.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
            {!lignes.length ? (
              <p className={etapes.aide}>{t('regie:offres.nouvelle.aucuneOeuvre')}</p>
            ) : null}
            {lignes.map((ligne, index) => (
              <div className={styles.ligneFormulaire} key={`${ligne.oeuvreId}:${index}`}>
                <div className={etapes.titreLigne}>
                  <strong>{ligne.titre}</strong>
                  {ligne.prixPublicCentimes != null ? (
                    <span>
                      {t('regie:offres.nouvelle.prixDemande', {
                        prix: formatPrice(ligne.prixPublicCentimes, devise, langueInterface),
                      })}
                    </span>
                  ) : null}
                </div>
                {ligne.editionSuivie ? (
                  <ChoixExemplaire
                    galerieSlug={galerieSlug}
                    ligne={ligne}
                    index={index}
                    onChoisir={(exemplaireId, prix) =>
                      remplacer(index, {
                        exemplaireId,
                        ...(prix === null
                          ? {}
                          : { prixProposeCentimes: prix, prixPublicCentimes: prix }),
                      })
                    }
                  />
                ) : null}
                <Button
                  variant="ghost"
                  aria-label={t('regie:offres.nouvelle.enlever', { titre: ligne.titre })}
                  onClick={() => setLignes(lignes.filter((_, i) => i !== index))}
                >
                  {t('regie:offres.nouvelle.enlever', { titre: ligne.titre })}
                </Button>
              </div>
            ))}
          </>
        ) : null}
        {etape === 'contact' ? (
          contact ? (
            <div className={styles.contactChoisi}>
              <span>{contact.nom}</span>
              <Button variant="ghost" onClick={() => setContact(null)}>
                {t('regie:offres.nouvelle.changerContact')}
              </Button>
            </div>
          ) : (
            <>
              <Field htmlFor="offre-contact" label={t('regie:offres.nouvelle.contact')}>
                <Input
                  id="offre-contact"
                  type="search"
                  value={rechercheContact}
                  onChange={(e) => setRechercheContact(e.target.value)}
                />
              </Field>
              {contacts.data?.contacts
                .filter((c) => !c.archive && !c.anonymise)
                .map((c) => (
                  <Button
                    key={c.id}
                    variant="ghost"
                    onClick={() => {
                      setContact({ id: c.id, nom: c.nomAffiche });
                      setLangue(c.langue);
                    }}
                  >
                    {c.nomAffiche}
                  </Button>
                ))}
              {contacts.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
            </>
          )
        ) : null}
        {etape === 'montant' ? (
          <>
            {lignes.map((ligne, index) => {
              const ecart = ecartAuPrixDemande([
                {
                  prixPublicCentimes: ligne.prixPublicCentimes ?? null,
                  prixProposeCentimes: ligne.prixProposeCentimes,
                },
              ]);
              const remiseMax = remiseMaxDe(ligne);
              const remisePoints = ecart
                ? Math.round((-ecart.ecartCentimes / ecart.demandeCentimes) * POINTS_DE_BASE)
                : 0;
              const auDessus = remiseMax !== null && remisePoints > remiseMax;
              return (
                <div className={styles.ligneFormulaire} key={`${ligne.oeuvreId}:${index}`}>
                  <div className={etapes.titreLigne}>
                    <strong>{ligne.titre}</strong>
                    <span>
                      {ligne.prixPublicCentimes != null
                        ? t('regie:offres.nouvelle.prixDemande', {
                            prix: formatPrice(ligne.prixPublicCentimes, devise, langueInterface),
                          })
                        : t('regie:offres.nouvelle.sansPrixDemande')}
                    </span>
                    {remiseMax !== null && ligne.prixPublicCentimes != null ? (
                      <span>
                        {t('regie:offres.nouvelle.remiseMax', {
                          pourcent: formatNumber(remiseMax / 100, langueInterface, 1),
                          plancher: formatPrice(
                            Math.round(
                              (ligne.prixPublicCentimes * (POINTS_DE_BASE - remiseMax)) /
                                POINTS_DE_BASE,
                            ),
                            devise,
                            langueInterface,
                          ),
                        })}
                      </span>
                    ) : null}
                  </div>
                  <Field
                    htmlFor={`offre-prix-${index}`}
                    label={t('regie:offres.nouvelle.prix', { titre: ligne.titre })}
                  >
                    <PriceInput
                      id={`offre-prix-${index}`}
                      cents={ligne.prixProposeCentimes}
                      lang={langueInterface}
                      onCentsChange={(prix) => remplacer(index, { prixProposeCentimes: prix ?? 0 })}
                    />
                    {ecart ? (
                      <p
                        className={etapes.ecart}
                        data-alerte={auDessus ? 'true' : undefined}
                        role="status"
                      >
                        {ecart.ecartCentimes === 0
                          ? t('regie:offres.carte.ecart.egal')
                          : t(
                              ecart.ecartCentimes < 0
                                ? 'regie:offres.nouvelle.remiseActuelle'
                                : 'regie:offres.nouvelle.majoration',
                              {
                                pourcent: formatNumber(
                                  Math.abs(ecart.pourcent),
                                  langueInterface,
                                  1,
                                ),
                                montant: formatPrice(
                                  Math.abs(ecart.ecartCentimes),
                                  devise,
                                  langueInterface,
                                ),
                              },
                            )}
                        {auDessus ? ` ${t('regie:offres.nouvelle.auDessusRemiseMax')}` : ''}
                      </p>
                    ) : null}
                    {!offreInitiale &&
                    galerieAModule(galerie, 'offres-relances') &&
                    galerieALaction(galerie, 'offers:write') &&
                    (!ligne.editionSuivie || ligne.exemplaireId) ? (
                      <SuggestionPrixApprouve
                        contactId={contact?.id ?? null}
                        oeuvreId={ligne.oeuvreId}
                        exemplaireId={ligne.exemplaireId}
                        onAppliquer={(prixProposeCentimes) =>
                          remplacer(index, { prixProposeCentimes })
                        }
                      />
                    ) : null}
                  </Field>
                </div>
              );
            })}
            <p className={etapes.total}>
              <span>{t('regie:offres.fiche.total')}</span>
              <strong>{formatPrice(total, devise, langueInterface)}</strong>
            </p>
            <Field htmlFor="offre-validite" label={t('regie:offres.nouvelle.validite')}>
              <Input
                id="offre-validite"
                type="date"
                value={validite}
                onChange={(e) => setValidite(e.target.value)}
              />
            </Field>
            <Field htmlFor="offre-message" label={t('regie:offres.nouvelle.message')}>
              <Textarea
                id="offre-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Field>
            <Field htmlFor="offre-langue" label={t('regie:offres.nouvelle.langue')}>
              <Select
                id="offre-langue"
                value={langue ?? fiche.data?.contact.langue ?? 'fr'}
                onValueChange={(v) => setLangue(v as Langue)}
                options={[
                  { value: 'fr', label: 'Français' },
                  { value: 'en', label: 'English' },
                ]}
              />
            </Field>
          </>
        ) : null}
        {mutation.isError ? (
          <p role="alert">
            {estApiError(mutation.error) ? mutation.error.message : t('commun:erreurReseau')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function ChoixExemplaire({
  galerieSlug,
  ligne,
  index,
  onChoisir,
}: {
  galerieSlug: string;
  ligne: LigneChoisie;
  index: number;
  onChoisir: (id: string, prix: number | null) => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const editions = useEditionsOeuvre(galerieSlug, ligne.oeuvreId);
  const disponibles =
    editions.data?.editions.filter((e) =>
      ['disponible', 'reservee', 'en_depot', 'pretee'].includes(e.statut),
    ) ?? [];
  return (
    <Field
      htmlFor={`offre-exemplaire-${ligne.oeuvreId}-${index}`}
      label={t('regie:offres.nouvelle.exemplaire', { titre: ligne.titre })}
    >
      <Select
        id={`offre-exemplaire-${ligne.oeuvreId}-${index}`}
        value={ligne.exemplaireId ?? ''}
        options={disponibles.map((e) => ({ value: e.id, label: e.numero }))}
        onValueChange={(id) =>
          onChoisir(id, disponibles.find((e) => e.id === id)?.prixCentimes ?? null)
        }
      />
      {editions.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
    </Field>
  );
}
